// frontend/src/pages/AccountsIndex.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../lib/api";

type Account = {
  id: number | string;
  currency: string;
  balance?: number | null;
  available_balance?: number | null;
  iban?: string | null;
  label?: string | null;
  plan_label?: string | null;
};

type AccountsEnvelope =
  | { accounts?: Account[] }
  | { data?: Account[] }
  | Account[];

/**
 * Unwraps any of:
 * - [ ... ]
 * - { accounts: [ ... ] }
 * - { data: [ ... ] }
 */
function unwrapAccounts(res: AccountsEnvelope | any): Account[] {
  if (!res) return [];

  if (Array.isArray(res)) {
    return res as Account[];
  }

  if (Array.isArray((res as any).accounts)) {
    return (res as any).accounts as Account[];
  }

  if (Array.isArray((res as any).data)) {
    return (res as any).data as Account[];
  }

  return [];
}

export default function AccountsIndex() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState<string | null>(null);
  const serverUrl = "/accounts"; // classic Blade fallback

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const res = await apiGet<AccountsEnvelope>("/xapi/accounts");
        if (!mounted) return;
        setAccounts(unwrapAccounts(res));
        setError(null);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || "JSON endpoint unavailable");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl font-semibold mb-4">Funding Accounts</h1>
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card-box">
                <div className="skeleton h-4 w-1/3 mb-2 rounded" />
                <div className="skeleton h-3 w-1/2 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">Funding Accounts</h1>
          <Link to="/accounts/request" className="btn">
            + Request funding account
          </Link>
        </div>

        {/* Error fallback → link to Blade Funding Activity */}
        {error && (
          <div className="section-box p-4 mb-4">
            <div className="text-sm text-red-600 mb-2">
              Unable to load funding accounts via JSON ({error}).
            </div>
            <a href={serverUrl} className="btn btn-white text-sm">
              Open classic Funding Activity page
            </a>
          </div>
        )}

        {!error && accounts.length === 0 && (
          <div className="section-box p-4">
            <div className="text-sm text-gray-600">No funding accounts yet.</div>
          </div>
        )}

        {!error && accounts.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            {accounts.map((acc) => (
              <div key={acc.id} className="card-box">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{acc.currency} account</div>
                  {acc.plan_label && (
                    <Link
                      to={`/accounts/${acc.id}/change-plan`}
                      className="btn btn-white text-xs"
                    >
                      Change plan
                    </Link>
                  )}
                </div>
                {acc.iban && (
                  <div className="mt-2 text-sm text-gray-600">
                    IBAN: <span className="font-mono">{acc.iban}</span>
                  </div>
                )}
                <div className="mt-2 text-sm">
                  Funding Balance:{" "}
                  <span className="font-semibold">
                    {formatMoney(
                      acc.available_balance ?? acc.balance ?? undefined
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!error && (
          <div className="mt-6 text-sm text-gray-500">
            Need a statement? Go to a funding account and download PDF/CSV from
            your web browser.
          </div>
        )}
      </div>
    </div>
  );
}

function formatMoney(v?: number | null) {
  if (v === null || v === undefined || isNaN(v)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return String(v);
  }
}
